// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Alert, Paper, Stack } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { DataTableSortStatus } from 'mantine-datatable';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { PageHeader } from '../../components/layout/PageHeader';
import type { PageTabsItem } from '../../components/layout/PageTabs';
import { PageTabs } from '../../components/layout/PageTabs';
import { useRole } from '../../hooks/useRole';
import { hasPermission } from '../../utils/permissions';
import { SweAccessTab } from '../swe-access/SweAccessTab';
import { AccountTypesTable } from './AccountTypesTable';
import { StaffTable } from './StaffTable';
import { DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from './staffing-constants';
import type { StaffRow } from './staffing-utils';
import { useAllPractitionerRoles } from './useAllPractitionerRoles';
import { useStaffList } from './useStaffList';

const BASE_STAFFING_TABS: PageTabsItem[] = [
  { id: 'staffing', label: 'Staffing' },
  { id: 'analytics', label: 'Analytics', disabled: true },
];

const DEFAULT_SORT: DataTableSortStatus<StaffRow> = { columnAccessor: 'name', direction: 'asc' };

const DEFAULT_TAB = 'staffing';

function resolveStaffingTab(rawTab: string | null, tabs: PageTabsItem[], canManageSwe: boolean): string {
  if (rawTab === 'swe-access') {
    return canManageSwe ? 'swe-access' : DEFAULT_TAB;
  }
  if (rawTab && tabs.some((t) => t.id === rawTab && !t.disabled)) {
    return rawTab;
  }
  return DEFAULT_TAB;
}

export function StaffingPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<StaffRow>>(DEFAULT_SORT);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const { role, isAdmin } = useRole();
  const canMutate = hasPermission({ role, permission: 'manage-staff', isAdmin });
  const canManageSwe = canMutate;

  const staffingTabs = useMemo(() => {
    const tabs = [...BASE_STAFFING_TABS];
    if (canManageSwe) {
      tabs.push({ id: 'swe-access', label: 'SWE Access' });
    }
    return tabs;
  }, [canManageSwe]);

  const rawTab = searchParams.get('tab');
  const tab = resolveStaffingTab(rawTab, staffingTabs, canManageSwe);

  const setTab = (value: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === DEFAULT_TAB) {
          next.delete('tab');
        } else {
          next.set('tab', value);
        }
        return next;
      },
      { replace: true }
    );
  };

  // Reset to page 1 whenever the filter or sort changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are intentional triggers — the effect fires when filter/sort changes even though they are not read inside the body
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortStatus.columnAccessor, sortStatus.direction]);

  const query = {
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    sort: {
      // columnAccessor is typed as keyof StaffRow | string; narrow to the two
      // server-sortable columns. Unknown columns fall back to 'name'.
      column: (sortStatus.columnAccessor === 'lastUpdated' ? 'lastUpdated' : 'name') as 'name' | 'lastUpdated',
      direction: sortStatus.direction,
    },
    search: debouncedSearch,
  };

  const { rows, total, loading, error, refresh, reload } = useStaffList(query);
  const { practitionerRoles } = useAllPractitionerRoles();

  return (
    <Stack gap="md" m="md">
      <PageHeader title="Staffing" actions={<PageTabs tabs={staffingTabs} value={tab} onChange={setTab} />} />

      {tab === 'swe-access' && canManageSwe ? (
        <SweAccessTab />
      ) : (
        <>
          {error && (
            <Alert color="red" title="Could not load staff">
              {error}
            </Alert>
          )}

          <Paper withBorder p="lg" radius="md" shadow="sm">
            <StaffTable
              rows={rows}
              total={total}
              loading={loading}
              page={page}
              onPageChange={setPage}
              sortStatus={sortStatus}
              onSortStatusChange={setSortStatus}
              search={search}
              onSearchChange={setSearch}
              onRefresh={refresh}
              onReload={reload}
              canMutate={canMutate}
            />
          </Paper>

          <Paper withBorder p="lg" radius="md" shadow="sm">
            <AccountTypesTable practitionerRoles={practitionerRoles} />
          </Paper>
        </>
      )}
    </Stack>
  );
}
